import React from 'react'
import { graphql } from 'gatsby'
import styled from 'styled-components'

import Layout from '../components/Layout/Layout'
import Hero, { HeroVariantEnum } from '../components/Hero/Hero'
import pxToRem from '../utils/pxToRem'
import CardContact from '../components/CardContact/CardContact'
import BlockFinancialResult from '../components/BlockFinancialResult/BlockFinancialResult'
import Button from '../components/Button/Button'
import useCtaLink from '../hooks/useCtaLink'
import SideNavigation, { SideNavTypeEnum } from '../components/SideNavigation/SideNavigation'
import ButtonVariantEnum from '../enums/ButtonVariant'
import { deviceMax } from '../theme/breakpoints'
import CKEditorContent from '../components/CKEditorContent/CKEditorContent'

import Seo from '../components/Seo/Seo'
import { MainContainer } from '../theme/mainContainer.styled'
import NotFoundPage from '../pages/404'
import useMediaQuery from '../hooks/useMediaQuery'

const StyledBody = styled(MainContainer)`
  display: flex;
  flex-direction: row;
  justify-content: center;
`

const StyledLeftSide = styled.div`
  width: 100%;
  max-width: ${pxToRem(384)};
  margin-right: ${pxToRem(20)};
`

const StyledCardContacts = styled.div`
  margin: ${pxToRem(20)} 0 0 0;
`

const StyledRightSide = styled.div`
  margin: 0;
  width: 100%;
  max-width: ${pxToRem(792)};
  @media ${deviceMax.tablet} {
    width: 100%;
    margin: ${pxToRem(36)} ${pxToRem(10)} 0 ${pxToRem(10)};
  }
`

const StyledRichText = styled.div`
  margin-bottom: ${pxToRem(20)};
  width: 100%;
  @media ${deviceMax.tablet} {
    margin-left: ${pxToRem(10)};
    margin-right: ${pxToRem(10)};
  }
`

const StyledButton = styled.div`
  display: flex;
  justify-content: end;
  margin-bottom: ${pxToRem(20)};
  @media ${deviceMax.tablet} {
    margin-left: ${pxToRem(10)};
    margin-right: ${pxToRem(10)};
  }
  @media ${deviceMax.mobileL} {
    justify-content: center;
  }
`

const ContactsCardsContainerDesktop = styled.div`
  display: block;
  margin-top: ${pxToRem(20)};
  @media ${deviceMax.mobileL} {
    display: none;
  }
`

const ContactsCardsContainerMobile = styled.div`
  display: none;
  margin-bottom: ${pxToRem(20)};
  @media ${deviceMax.mobileL} {
    justify-content: center;
    display: flex;
  }
`

const StyledCardContact = styled(CardContact)`
  @media ${deviceMax.mobileL} {
    margin: 0 ${pxToRem(10)};
  }
`

const Financial = ({ data, location }) => {
  if (data.contentfulTemplatePageFinancial === null) {
    return <NotFoundPage location={location} />
  }

  const {
    pageTitle,
    backgroundColor,
    imageHeaderDesktop,
    imageHeaderMobile,
    investorCheckbox,
    ctaButton,
    blocTextHtml,
    financialResultsBlocs,
    seoMetaTitle,
    seoMetaDescription,
  } = data.contentfulTemplatePageFinancial

  const { contact } = data.contentfulPageInvestors

  const isMobile = useMediaQuery(deviceMax.tablet)

  const [ctaUrl, ctaSlug] =
    ctaButton && ctaButton.url ? useCtaLink(ctaButton.url) : [null, null]

  const cardContacts =
    contact?.map((contact) => (
      <StyledCardContact
        department={contact.department}
        email={contact.email}
        firstname={contact.firstname}
        lastname={contact.lastname}
        phone={contact.phone}
      />
    )) ?? null

  return (
    <Layout>
      <Seo
        title={seoMetaTitle ? seoMetaTitle.seoMetaTitle : pageTitle}
        description={
          seoMetaDescription ? seoMetaDescription.seoMetaDescription : pageTitle
        }
        canonicalHref={location.pathname}
      />
      <Hero
        backgroundColor={backgroundColor}
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        pathname={location.pathname}
        title={pageTitle}
        variant={HeroVariantEnum.investor}
      />
      <StyledBody>
        {!isMobile && investorCheckbox && (
          <StyledLeftSide>
            <SideNavigation variant={SideNavTypeEnum.investor} />
            {contact && (
              <ContactsCardsContainerDesktop>
                {cardContacts}
              </ContactsCardsContainerDesktop>
            )}
          </StyledLeftSide>
        )}
        <StyledRightSide>
          {contact && (
            <ContactsCardsContainerMobile>
              {cardContacts}
            </ContactsCardsContainerMobile>
          )}
          {ctaButton && (
            <StyledButton>
              <Button
                to={ctaSlug}
                href={ctaUrl}
                variant={ButtonVariantEnum.primary}
              >
                {ctaButton.label}
              </Button>
            </StyledButton>
          )}

          {blocTextHtml && blocTextHtml.content && (
            <StyledRichText>
              <CKEditorContent html={blocTextHtml.content} />
            </StyledRichText>
          )}

          {financialResultsBlocs.map((block, index) => (
            <BlockFinancialResult key={index} block={block} />
          ))}
        </StyledRightSide>
      </StyledBody>
    </Layout>
  )
}

export default Financial

export const query = graphql`
  query FinancialQuery($contentful_id: String!) {
    contentfulPageInvestors {
      contact {
          ...contactFragment
      }
    }
    contentfulTemplatePageFinancial(contentful_id: { eq: $contentful_id }) {
      id
      slug
      pageTitle
      seoMetaTitle {
        seoMetaTitle
      }
      seoMetaDescription {
        seoMetaDescription
      }
      investorCheckbox
      backgroundColor
      imageHeaderDesktop {
          ...imageHeaderFragment
      }
      imageHeaderMobile {
          ...imageHeaderFragment
      }
      ctaButton {
        url
        label
      }
      blocTextHtml {
        content
      }
      financialResultsBlocs {
        blocDescription {
          blocDescription
        }
        blocTitle
        financialTabs {
          tabTitle
          contents {
            ... on ContentfulComponentFinancialDocumentsBloc {
              blocTitle
              sections {
                blocTitle
                documents {
                    ...documentFragment
                }
              }
              documents {
                  ...documentFragment
              }
            }
          }
        }
      }
    }
  }
`
